import React, { useState, useRef } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import api from "../../services/api";
import toastError from "../../errors/toastError";

export const SUGGESTION_CATEGORIES = [
  { value: "improvement", label: "Melhoria" },
  { value: "bug", label: "Problema" },
  { value: "feature", label: "Nova funcionalidade" },
  { value: "other", label: "Outro" },
];

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  attachRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  fileName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 260,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const initialState = { category: "improvement", title: "", text: "" };

const SuggestionModal = ({ open, onClose }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(initialState);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    setForm(initialState);
    setFile(null);
    onClose();
  };

  // Lê o valor antes do setState: no React 16 o evento é reciclado (event
  // pooling) e e.target já vem null dentro do updater.
  const handleChange = (field) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    e.target.value = "";
    if (!selected) return;

    if (!selected.type.startsWith("image/")) {
      toast.error("O print precisa ser uma imagem.");
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      toast.error("O print deve ter no máximo 5 MB.");
      return;
    }
    setFile(selected);
  };

  const canSubmit = form.title.trim() && form.text.trim() && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const data = new FormData();
    data.append("category", form.category);
    data.append("title", form.title.trim());
    data.append("text", form.text.trim());
    // Tela em que o usuário estava ao abrir o modal.
    data.append("pageUrl", window.location.pathname + window.location.search);
    if (file) data.append("file", file);

    setSubmitting(true);
    try {
      await api.post("/suggestions", data);
      toast.success("Obrigado! Sua sugestão foi enviada para a equipe.");
      setSubmitting(false);
      handleClose();
    } catch (err) {
      setSubmitting(false);
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Sugerir melhoria</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" className={classes.field}>
            Encontrou algo que pode melhorar ou um problema? Conte para a gente —
            toda sugestão é lida pela equipe do Message Express.
          </Typography>

          <FormControl variant="outlined" margin="dense" fullWidth className={classes.field}>
            <InputLabel id="suggestion-category-label">Tipo</InputLabel>
            <Select
              labelId="suggestion-category-label"
              label="Tipo"
              value={form.category}
              onChange={handleChange("category")}
            >
              {SUGGESTION_CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Título"
            placeholder="Resumo em poucas palavras"
            variant="outlined"
            margin="dense"
            fullWidth
            required
            autoFocus
            value={form.title}
            onChange={handleChange("title")}
            inputProps={{ maxLength: 120 }}
            className={classes.field}
          />

          <TextField
            label="Descrição"
            placeholder="O que acontece hoje e o que você gostaria que acontecesse?"
            variant="outlined"
            margin="dense"
            fullWidth
            required
            multiline
            minRows={5}
            value={form.text}
            onChange={handleChange("text")}
            inputProps={{ maxLength: 5000 }}
            className={classes.field}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <div className={classes.attachRow}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
            >
              {file ? "Trocar print" : "Anexar print (opcional)"}
            </Button>
            {file && (
              <>
                <Typography variant="body2" className={classes.fileName}>
                  {file.name}
                </Typography>
                <IconButton size="small" onClick={() => setFile(null)} disabled={submitting}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined" disabled={submitting}>
            Cancelar
          </Button>
          <div className={classes.btnWrapper}>
            <Button type="submit" color="primary" variant="contained" disabled={!canSubmit}>
              Enviar sugestão
            </Button>
            {submitting && <CircularProgress size={24} className={classes.buttonProgress} />}
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SuggestionModal;
